<script setup lang="ts">
import { Video, FileText, ListChecks, Bookmark } from 'lucide-vue-next'

interface Stats {
  videos: number
  videosNew?: number
  videosProgress?: number
  materials: number
  materialsNew?: number
  materialsProgress?: number
  assignments: number
  assignmentsProgress?: number
  saved: number
  savedLastUpdate?: string
}

defineProps<{
  stats: Stats
}>()
</script>

<template>
  <div class="grid grid-cols-2 lg:grid-cols-4 gap-6">
    <!-- Video Lessons -->
    <div class="bg-white rounded-xl p-4 border border-gray-200">
      <div class="flex items-center justify-between mb-3">
        <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <Video class="w-5 h-5 text-blue-600" />
        </div>
        <span v-if="stats.videosNew" class="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">+{{ stats.videosNew }}</span>
      </div>
      <h3 class="text-2xl font-bold text-gray-900 mb-0.5">{{ stats.videos }}</h3>
      <p class="text-gray-500 text-xs">Video Lessons</p>
      <div v-if="stats.videosProgress !== undefined" class="mt-3 flex items-center text-xs text-gray-500">
        <div class="flex-1 bg-gray-200 rounded-full h-1.5">
          <div class="bg-blue-600 h-1.5 rounded-full" :style="{ width: `${stats.videosProgress}%` }"></div>
        </div>
        <span class="ml-2">{{ stats.videosProgress }}%</span>
      </div>
    </div>

    <!-- Study Materials -->
    <div class="bg-white rounded-xl p-4 border border-gray-200">
      <div class="flex items-center justify-between mb-3">
        <div class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <FileText class="w-5 h-5 text-purple-600" />
        </div>
        <span v-if="stats.materialsNew" class="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">+{{ stats.materialsNew }}</span>
      </div>
      <h3 class="text-2xl font-bold text-gray-900 mb-0.5">{{ stats.materials }}</h3>
      <p class="text-gray-500 text-xs">Study Materials</p>
      <div v-if="stats.materialsProgress !== undefined" class="mt-3 flex items-center text-xs text-gray-500">
        <div class="flex-1 bg-gray-200 rounded-full h-1.5">
          <div class="bg-purple-600 h-1.5 rounded-full" :style="{ width: `${stats.materialsProgress}%` }"></div>
        </div>
        <span class="ml-2">{{ stats.materialsProgress }}%</span>
      </div>
    </div>

    <!-- Assignments -->
    <div class="bg-white rounded-xl p-4 border border-gray-200">
      <div class="flex items-center justify-between mb-3">
        <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <ListChecks class="w-5 h-5 text-green-600" />
        </div>
        <span class="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Active</span>
      </div>
      <h3 class="text-2xl font-bold text-gray-900 mb-0.5">{{ stats.assignments }}</h3>
      <p class="text-gray-500 text-xs">Assignments</p>
      <div v-if="stats.assignmentsProgress !== undefined" class="mt-3 flex items-center text-xs text-gray-500">
        <div class="flex-1 bg-gray-200 rounded-full h-1.5">
          <div class="bg-green-600 h-1.5 rounded-full" :style="{ width: `${stats.assignmentsProgress}%` }"></div>
        </div>
        <span class="ml-2">{{ stats.assignmentsProgress }}%</span>
      </div>
    </div>

    <!-- Saved Items -->
    <div class="bg-white rounded-xl p-4 border border-gray-200">
      <div class="flex items-center justify-between mb-3">
        <div class="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
          <Bookmark class="w-5 h-5 text-orange-500" />
        </div>
        <span class="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">{{ stats.saved }}</span>
      </div>
      <h3 class="text-2xl font-bold text-gray-900 mb-0.5">{{ stats.saved }}</h3>
      <p class="text-gray-500 text-xs">Saved Items</p>
      <div class="mt-3">
        <p class="text-xs text-gray-400">{{ stats.savedLastUpdate || '2 hours ago' }}</p>
      </div>
    </div>
  </div>
</template>
